import React, { useCallback, useEffect, useState } from "react";
import { api, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import {
  Plus,
  MagnifyingGlass,
  User,
  PencilSimple,
  Trash,
  ListBullets,
} from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Customers() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: "",
    mobile: "",
    email: "",
    address: "",
    dob: "",
    anniversary: "",
    gst_number: "",
  });
  const [ledger, setLedger] = useState(null);
  const [ledgerOpen, setLedgerOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/customers${q ? `?q=${encodeURIComponent(q)}` : ""}`,
      );
      setList(data);
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({
      name: "",
      mobile: "",
      email: "",
      address: "",
      dob: "",
      anniversary: "",
      gst_number: "",
    });
    setOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name || "",
      mobile: c.mobile || "",
      email: c.email || "",
      address: c.address || "",
      dob: c.dob || "",
      anniversary: c.anniversary || "",
      gst_number: c.gst_number || "",
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      if (!form.name.trim() || !form.mobile.trim()) {
        toast.error("Name and mobile are required");
        return;
      }
      if (saving) return;
      setSaving(true);
      const payload = { ...form };
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "") payload[k] = null;
      });
      payload.name = form.name;
      payload.mobile = form.mobile;
      if (editing) await api.put(`/customers/${editing.id}`, payload);
      else await api.post("/customers", payload);
      toast.success(editing ? "Updated" : "Created");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.name}?`)) return;
    await api.delete(`/customers/${c.id}`);
    toast.success("Deleted");
    load();
  };

  const showLedger = async (c) => {
    const { data } = await api.get(`/customers/${c.id}/ledger`);
    setLedger({ customer: c, ...data });
    setLedgerOpen(true);
  };

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="customers-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            CRM
          </div>
          <h1 className="font-display text-3xl md:text-4xl tracking-tight mt-1">
            Customers
          </h1>
        </div>
        <Button
          data-testid="add-customer-btn"
          onClick={openCreate}
          className="bg-primary text-primary-foreground hover:bg-primary/90"
        >
          <Plus size={16} className="mr-1.5" /> New customer
        </Button>
      </header>

      <div className="relative max-w-md">
        <MagnifyingGlass
          size={16}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
        />
        <Input
          data-testid="customer-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name or mobile..."
          className="pl-9 bg-card border-border"
        />
      </div>

      <div className="rounded-lg border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/40 hover:bg-secondary/40">
              <TableHead className="uppercase text-[10px] tracking-[0.18em]">
                Customer
              </TableHead>
              <TableHead className="uppercase text-[10px] tracking-[0.18em]">
                Mobile
              </TableHead>
              <TableHead className="uppercase text-[10px] tracking-[0.18em]">
                Email
              </TableHead>
              <TableHead className="uppercase text-[10px] tracking-[0.18em]">
                GST
              </TableHead>
              <TableHead className="uppercase text-[10px] tracking-[0.18em] text-right">
                Loyalty
              </TableHead>
              <TableHead className="uppercase text-[10px] tracking-[0.18em] text-right">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={6}
                  className="text-center py-10 text-muted-foreground"
                >
                  Loading...
                </TableCell>
              </TableRow>
            ) : list.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={6}
                  className="text-center py-10 text-muted-foreground"
                >
                  No customers yet.
                </TableCell>
              </TableRow>
            ) : (
              list.map((c) => (
                <TableRow key={c.id} data-testid={`customer-row-${c.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-full bg-primary/10 border border-primary/30 grid place-items-center text-gold">
                        <User size={14} weight="duotone" />
                      </div>
                      <div>
                        <div className="font-medium">{c.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {c.address || "—"}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="tabular-nums">{c.mobile}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {c.email || "—"}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {c.gst_number || "—"}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {c.loyalty_points || 0}
                  </TableCell>
                  <TableCell className="text-right">
                    <button
                      onClick={() => showLedger(c)}
                      className="p-1.5 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground mr-1"
                      title="Ledger"
                    >
                      <ListBullets size={14} />
                    </button>
                    <button
                      onClick={() => openEdit(c)}
                      className="p-1.5 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground mr-1"
                      title="Edit"
                    >
                      <PencilSimple size={14} />
                    </button>
                    <button
                      onClick={() => remove(c)}
                      className="p-1.5 rounded-md hover:bg-destructive/15 text-muted-foreground hover:text-destructive"
                      title="Delete"
                    >
                      <Trash size={14} />
                    </button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              {editing ? "Edit" : "New"} Customer
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { k: "name", label: "Name *" },
              { k: "mobile", label: "Mobile *" },
              { k: "email", label: "Email" },
              { k: "gst_number", label: "GST Number" },
              { k: "address", label: "Address" },
              { k: "dob", label: "Date of Birth", type: "date" },
              { k: "anniversary", label: "Anniversary", type: "date" },
            ].map((f) => (
              <div
                key={f.k}
                className={f.k === "address" ? "md:col-span-2" : ""}
              >
                <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                  {f.label}
                </Label>
                <Input
                  data-testid={`field-customer-${f.k}`}
                  type={f.type || "text"}
                  className="mt-1.5 bg-background border-border"
                  value={form[f.k] ?? ""}
                  onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              data-testid="save-customer-btn"
              onClick={save}
              loading={saving}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={ledgerOpen} onOpenChange={setLedgerOpen}>
        <DialogContent className="bg-card border-border max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              Ledger — {ledger?.customer?.name}
            </DialogTitle>
          </DialogHeader>
          {ledger && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-lg border border-border p-3">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                    Total sales
                  </div>
                  <div className="font-display text-lg mt-1 tabular-nums">
                    {inr(ledger.total_sales)}
                  </div>
                </div>
                <div className="rounded-lg border border-border p-3">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                    Paid
                  </div>
                  <div className="font-display text-lg mt-1 tabular-nums">
                    {inr(ledger.total_paid)}
                  </div>
                </div>
                <div className="rounded-lg border border-border p-3">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                    Outstanding
                  </div>
                  <div className="font-display text-lg mt-1 tabular-nums text-gold">
                    {inr(ledger.outstanding)}
                  </div>
                </div>
              </div>
              <div className="max-h-72 overflow-y-auto rounded-lg border border-border">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-secondary/40 hover:bg-secondary/40">
                      <TableHead>Invoice</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead className="text-right">Paid</TableHead>
                      <TableHead className="text-right">Balance</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ledger.bills.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={5}
                          className="text-center text-muted-foreground py-6"
                        >
                          No bills yet.
                        </TableCell>
                      </TableRow>
                    ) : (
                      ledger.bills.map((b) => (
                        <TableRow key={b.id}>
                          <TableCell className="font-mono text-xs">
                            {b.invoice_no}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {(b.created_at || "").slice(0, 10)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {inr(b.grand_total)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {inr(b.paid)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {inr(b.balance)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
